import { GraphData, GraphLink, GraphNode } from '../types';

type Row = Record<string, unknown>;

export const COLLECTION_TYPE_FIELD = '__increment_collection_type__';
const ALL_NODE_COLLECTIONS = '__increment_all_node_collections__';
const ALL_EDGE_COLLECTIONS = '__increment_all_edge_collections__';

export interface ImportCollection {
  key: string;
  label: string;
  rows: Row[];
  fields: string[];
}

export interface SchemaMapping {
  nodeCollectionKey: string;
  edgeCollectionKey: string;
  nodeIdField: string;
  nodeTypeField: string;
  edgeSourceField: string;
  edgeTargetField: string;
  edgeTypeField: string;
}

export interface GraphImportDraft {
  sourceName: string;
  formatLabel: string;
  nodeCollections: ImportCollection[];
  edgeCollections: ImportCollection[];
  mapping: SchemaMapping;
  warnings: string[];
}

export interface GraphImportResult {
  graphData: GraphData;
  warnings: string[];
}

const NODE_KEYS = ['nodes', 'node', 'vertices', 'vertex', 'entities', 'entity'];
const EDGE_KEYS = ['links', 'edges', 'relationships', 'relationship', 'relations', 'relation'];

const NODE_ID_FIELDS = ['id', 'uuid', '_id', 'key', 'elementId', 'element_id', 'identity'];
const NODE_TYPE_FIELDS = ['Node Type', 'nodeType', 'node_type', 'type', 'label', 'labels', 'category', 'kind'];
const EDGE_SOURCE_FIELDS = ['source', 'sourceId', 'source_id', 'start', 'startId', 'start_id', 'startNode', 'startNodeId', 'from', 'from_id', '_start'];
const EDGE_TARGET_FIELDS = ['target', 'targetId', 'target_id', 'end', 'endId', 'end_id', 'endNode', 'endNodeId', 'to', 'to_id', '_end'];
const EDGE_TYPE_FIELDS = ['Edge Type', 'edgeType', 'edge_type', 'type', 'label', 'relationship', 'relationshipType', 'relationship_type', 'kind'];

const isRecord = (value: unknown): value is Row => (
  !!value && typeof value === 'object' && !Array.isArray(value)
);

const neo4jIntegerToString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (isRecord(value)) {
    const low = value.low;
    const high = value.high;
    if (typeof low === 'number' && (high === 0 || high === undefined)) {
      return String(low);
    }
    if (value.id !== undefined) return neo4jIntegerToString(value.id);
    if (value.elementId !== undefined) return neo4jIntegerToString(value.elementId);
  }
  return null;
};

const valueToScalar = (value: unknown): unknown => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) {
    return value.map(item => String(valueToScalar(item) ?? '')).filter(Boolean).join(', ');
  }
  if (isRecord(value)) {
    const neo4jValue = neo4jIntegerToString(value);
    return neo4jValue ?? JSON.stringify(value);
  }
  return String(value);
};

const valueToLabel = (value: unknown, fallback: string): string => {
  const scalar = valueToScalar(value);
  const label = String(scalar ?? '').trim();
  return label || fallback;
};

const copyFlatProperties = (source: Row, reservedKeys: Set<string> = new Set()): Row => {
  const target: Row = {};
  Object.entries(source).forEach(([key, value]) => {
    if (reservedKeys.has(key) || key === COLLECTION_TYPE_FIELD) return;
    target[key] = valueToScalar(value);
  });
  return target;
};

const collectionLabelFromKey = (key: string): string => {
  const last = key.split('.').pop() || key;
  return last.replace(/[_-]+/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

const getFields = (rows: Row[]): string[] => {
  const fields = new Set<string>();
  rows.slice(0, 200).forEach(row => {
    Object.keys(row).forEach(key => {
      if (key !== COLLECTION_TYPE_FIELD) fields.add(key);
    });
  });
  return Array.from(fields).sort((a, b) => a.localeCompare(b));
};

const makeCollection = (key: string, label: string, rows: Row[]): ImportCollection => ({
  key,
  label,
  rows,
  fields: getFields(rows)
});

const withAllCollection = (
  collections: ImportCollection[],
  key: string,
  label: string
): ImportCollection[] => {
  if (collections.length <= 1) return collections;

  const rows: Row[] = [];
  collections.forEach(collection => {
    collection.rows.forEach(row => {
      rows.push({
        ...row,
        [COLLECTION_TYPE_FIELD]: collection.label
      });
    });
  });

  return [makeCollection(key, label, rows), ...collections];
};

const findField = (fields: string[], candidates: string[]): string => {
  const exact = candidates.find(candidate => fields.includes(candidate));
  if (exact) return exact;

  const lowerFields = fields.map(field => ({ field, normalized: field.toLowerCase().replace(/[^a-z0-9]/g, '') }));
  const lowerCandidates = candidates.map(candidate => candidate.toLowerCase().replace(/[^a-z0-9]/g, ''));
  const fuzzy = lowerFields.find(({ normalized }) => lowerCandidates.includes(normalized));
  return fuzzy?.field || '';
};

const hasFieldPair = (fields: string[], sources: string[], targets: string[]): boolean => (
  !!findField(fields, sources) && !!findField(fields, targets)
);

const keyMatches = (key: string, hints: string[]): boolean => {
  const lower = key.toLowerCase();
  return hints.some(hint => lower === hint || lower.endsWith(`.${hint}`) || lower.includes(hint));
};

const collectObjectArrays = (value: unknown, prefix: string, output: ImportCollection[], depth = 0) => {
  if (depth > 4) return;

  if (Array.isArray(value)) {
    const rows = value.filter(isRecord);
    if (rows.length > 0) {
      const key = prefix || 'rows';
      output.push(makeCollection(key, collectionLabelFromKey(key), rows));
    }
    return;
  }

  if (!isRecord(value)) return;

  Object.entries(value).forEach(([key, child]) => {
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    collectObjectArrays(child, nextPrefix, output, depth + 1);
  });
};

const getEndpointId = (value: unknown): string | null => {
  const direct = neo4jIntegerToString(value);
  if (direct !== null) return direct;
  if (isRecord(value)) {
    return neo4jIntegerToString(value.id) ||
      neo4jIntegerToString(value.elementId) ||
      neo4jIntegerToString(value.identity) ||
      null;
  }
  return null;
};

const normalizeNeo4jNode = (node: Row): Row => {
  const properties = isRecord(node.properties) ? node.properties : {};
  const labels = Array.isArray(node.labels)
    ? node.labels
    : node.label !== undefined
      ? [node.label]
      : [];
  const row = copyFlatProperties(properties);
  const nodeId = getEndpointId(node.id) ||
    getEndpointId(node.elementId) ||
    getEndpointId(node.identity) ||
    getEndpointId(properties.id);

  if (nodeId !== null) row.id = nodeId;
  if (node.elementId !== undefined) row.elementId = valueToScalar(node.elementId);
  row['Node Type'] = row['Node Type'] || valueToLabel(labels, 'Node');
  return row;
};

const normalizeNeo4jRelationship = (relationship: Row): Row => {
  const properties = isRecord(relationship.properties) ? relationship.properties : {};
  const row = copyFlatProperties(properties);
  const source = getEndpointId(relationship.source) ||
    getEndpointId(relationship.start) ||
    getEndpointId(relationship.startNode) ||
    getEndpointId(relationship.startNodeId) ||
    getEndpointId(relationship.startNodeElementId);
  const target = getEndpointId(relationship.target) ||
    getEndpointId(relationship.end) ||
    getEndpointId(relationship.endNode) ||
    getEndpointId(relationship.endNodeId) ||
    getEndpointId(relationship.endNodeElementId);
  const edgeType = relationship['Edge Type'] ||
    relationship.edgeType ||
    relationship.label ||
    relationship.relationshipType ||
    relationship.type;

  if (relationship.id !== undefined) row.id = valueToScalar(relationship.id);
  if (source !== null) row.source = source;
  if (target !== null) row.target = target;
  row['Edge Type'] = valueToLabel(edgeType, 'RELATED');
  return row;
};

const collectionFromNeo4jEntityMap = (value: unknown): Row[] => {
  if (Array.isArray(value)) return value.filter(isRecord);
  if (!isRecord(value)) return [];
  return Object.values(value).filter(isRecord);
};

const collectNeo4jGraphs = (
  value: unknown,
  nodes: Row[],
  relationships: Row[],
  depth = 0
) => {
  if (depth > 6 || !value) return;

  if (Array.isArray(value)) {
    value.forEach(item => collectNeo4jGraphs(item, nodes, relationships, depth + 1));
    return;
  }

  if (!isRecord(value)) return;

  const graph = isRecord(value.graph) ? value.graph : value;
  const graphNodes = collectionFromNeo4jEntityMap(graph.nodes);
  const graphRelationships = collectionFromNeo4jEntityMap(graph.relationships).length > 0
    ? collectionFromNeo4jEntityMap(graph.relationships)
    : collectionFromNeo4jEntityMap(graph.edges).length > 0
      ? collectionFromNeo4jEntityMap(graph.edges)
      : collectionFromNeo4jEntityMap(graph.rels);

  const looksLikeNeo4jGraph = graphNodes.some(node => Array.isArray(node.labels) || isRecord(node.properties)) ||
    graphRelationships.some(rel => rel.startNode !== undefined || rel.endNode !== undefined || rel.type !== undefined);

  if (graphNodes.length > 0 && graphRelationships.length > 0 && looksLikeNeo4jGraph) {
    nodes.push(...graphNodes.map(normalizeNeo4jNode));
    relationships.push(...graphRelationships.map(normalizeNeo4jRelationship));
    return;
  }

  Object.values(value).forEach(child => collectNeo4jGraphs(child, nodes, relationships, depth + 1));
};

const splitApocRows = (rows: Row[]): { nodes: Row[]; relationships: Row[] } => {
  const nodes: Row[] = [];
  const relationships: Row[] = [];

  rows.forEach(row => {
    const kind = String(row.type || row.kind || '').toLowerCase();
    if (kind === 'node') {
      nodes.push(normalizeNeo4jNode(row));
    } else if (kind === 'relationship' || kind === 'rel' || kind === 'edge') {
      relationships.push(normalizeNeo4jRelationship(row));
    }
  });

  return { nodes, relationships };
};

const classifyCollections = (collections: ImportCollection[]) => {
  const nodeCollections: ImportCollection[] = [];
  const edgeCollections: ImportCollection[] = [];

  collections.forEach(collection => {
    const fields = collection.fields;
    const edgeLike = keyMatches(collection.key, EDGE_KEYS) || hasFieldPair(fields, EDGE_SOURCE_FIELDS, EDGE_TARGET_FIELDS);
    const nodeLike = keyMatches(collection.key, NODE_KEYS);

    if (edgeLike && !nodeLike) {
      edgeCollections.push(collection);
    } else if (nodeLike) {
      nodeCollections.push(collection);
    }
  });

  if (nodeCollections.length === 0) {
    collections
      .filter(collection => !edgeCollections.includes(collection))
      .forEach(collection => nodeCollections.push(collection));
  }

  if (edgeCollections.length === 0) {
    collections
      .filter(collection => hasFieldPair(collection.fields, EDGE_SOURCE_FIELDS, EDGE_TARGET_FIELDS))
      .forEach(collection => edgeCollections.push(collection));
  }

  return { nodeCollections, edgeCollections };
};

const guessMapping = (
  nodeCollections: ImportCollection[],
  edgeCollections: ImportCollection[]
): SchemaMapping => {
  const nodeCollection = nodeCollections[0];
  const edgeCollection = edgeCollections[0];
  const nodeFields = nodeCollection?.fields || [];
  const edgeFields = edgeCollection?.fields || [];

  return {
    nodeCollectionKey: nodeCollection?.key || '',
    edgeCollectionKey: edgeCollection?.key || '',
    nodeIdField: findField(nodeFields, NODE_ID_FIELDS) || nodeFields[0] || '',
    nodeTypeField: findField(nodeFields, NODE_TYPE_FIELDS) || COLLECTION_TYPE_FIELD,
    edgeSourceField: findField(edgeFields, EDGE_SOURCE_FIELDS) || edgeFields[0] || '',
    edgeTargetField: findField(edgeFields, EDGE_TARGET_FIELDS) || edgeFields[1] || '',
    edgeTypeField: findField(edgeFields, EDGE_TYPE_FIELDS) || COLLECTION_TYPE_FIELD
  };
};

const parseJsonOrNdjson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch (jsonError) {
    const rows = text
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean)
      .map(line => JSON.parse(line));

    if (rows.length === 0) {
      throw jsonError;
    }
    return rows;
  }
};

export const parseGraphImportText = (text: string, sourceName = 'graph.json'): GraphImportDraft => {
  const raw = parseJsonOrNdjson(text.trim());
  return createGraphImportDraft(raw, sourceName);
};

export const parseGraphImportFile = async (file: File): Promise<GraphImportDraft> => {
  const text = await file.text();
  return parseGraphImportText(text, file.name);
};

export const createGraphImportDraft = (raw: unknown, sourceName = 'graph.json'): GraphImportDraft => {
  const warnings: string[] = [];
  const neo4jNodes: Row[] = [];
  const neo4jRelationships: Row[] = [];

  collectNeo4jGraphs(raw, neo4jNodes, neo4jRelationships);

  if (neo4jNodes.length > 0 && neo4jRelationships.length > 0) {
    const nodeCollections = [makeCollection('neo4j.nodes', 'Neo4j Nodes', neo4jNodes)];
    const edgeCollections = [makeCollection('neo4j.relationships', 'Neo4j Relationships', neo4jRelationships)];
    return {
      sourceName,
      formatLabel: 'Neo4j graph JSON',
      nodeCollections,
      edgeCollections,
      mapping: guessMapping(nodeCollections, edgeCollections),
      warnings
    };
  }

  if (Array.isArray(raw)) {
    const rows = raw.filter(isRecord);
    const apoc = splitApocRows(rows);
    if (apoc.nodes.length > 0 && apoc.relationships.length > 0) {
      const nodeCollections = [makeCollection('apoc.nodes', 'APOC Nodes', apoc.nodes)];
      const edgeCollections = [makeCollection('apoc.relationships', 'APOC Relationships', apoc.relationships)];
      return {
        sourceName,
        formatLabel: 'Neo4j APOC JSON',
        nodeCollections,
        edgeCollections,
        mapping: guessMapping(nodeCollections, edgeCollections),
        warnings
      };
    }
  }

  const collections: ImportCollection[] = [];
  collectObjectArrays(raw, '', collections);
  const objectCollections = collections.filter(collection => collection.rows.length > 0);

  if (objectCollections.length === 0) {
    throw new Error('No object arrays found. Upload JSON with node and edge collections.');
  }

  const classified = classifyCollections(objectCollections);
  const nodeCollections = withAllCollection(
    classified.nodeCollections,
    ALL_NODE_COLLECTIONS,
    'All Detected Node Collections'
  );
  const edgeCollections = withAllCollection(
    classified.edgeCollections,
    ALL_EDGE_COLLECTIONS,
    'All Detected Edge Collections'
  );

  if (nodeCollections.length === 0) {
    warnings.push('No node collection was detected automatically.');
  }
  if (edgeCollections.length === 0) {
    warnings.push('No edge collection was detected automatically.');
  }

  return {
    sourceName,
    formatLabel: 'Generic graph JSON',
    nodeCollections,
    edgeCollections,
    mapping: guessMapping(nodeCollections, edgeCollections),
    warnings
  };
};

const getCollection = (collections: ImportCollection[], key: string): ImportCollection | null => (
  collections.find(collection => collection.key === key) || null
);

const readMappedValue = (row: Row, field: string, fallback: string): unknown => (
  field === COLLECTION_TYPE_FIELD ? row[COLLECTION_TYPE_FIELD] || fallback : row[field]
);

export const normalizeGraphFromImportDraft = (
  draft: GraphImportDraft,
  mapping: SchemaMapping
): GraphImportResult => {
  const nodeCollection = getCollection(draft.nodeCollections, mapping.nodeCollectionKey);
  const edgeCollection = getCollection(draft.edgeCollections, mapping.edgeCollectionKey);
  const warnings = [...draft.warnings];

  if (!nodeCollection) {
    throw new Error('Choose a node collection before importing.');
  }
  if (!edgeCollection) {
    throw new Error('Choose an edge collection before importing.');
  }
  if (!mapping.nodeIdField) {
    throw new Error('Choose the node ID field before importing.');
  }
  if (!mapping.edgeSourceField || !mapping.edgeTargetField) {
    throw new Error('Choose source and target fields before importing.');
  }

  const nodeIds = new Set<string>();
  const nodes: GraphNode[] = [];

  nodeCollection.rows.forEach((row, index) => {
    const rawId = readMappedValue(row, mapping.nodeIdField, nodeCollection.label);
    const id = valueToLabel(rawId, `node-${index}`);
    if (nodeIds.has(id)) {
      warnings.push(`Skipped duplicate node id "${id}".`);
      return;
    }

    const rawType = readMappedValue(row, mapping.nodeTypeField, nodeCollection.label);
    const nodeType = valueToLabel(rawType, nodeCollection.label || 'Node');
    const properties = copyFlatProperties(row, new Set([mapping.nodeIdField, mapping.nodeTypeField]));

    nodes.push({
      ...properties,
      id,
      'Node Type': nodeType
    } as GraphNode);
    nodeIds.add(id);
  });

  const links: GraphLink[] = [];

  edgeCollection.rows.forEach((row, index) => {
    const source = valueToLabel(readMappedValue(row, mapping.edgeSourceField, edgeCollection.label), '');
    const target = valueToLabel(readMappedValue(row, mapping.edgeTargetField, edgeCollection.label), '');
    if (!source || !target) {
      warnings.push(`Skipped edge ${index + 1} because source or target is missing.`);
      return;
    }

    const edgeType = valueToLabel(readMappedValue(row, mapping.edgeTypeField, edgeCollection.label), edgeCollection.label || 'RELATED');
    const properties = copyFlatProperties(row, new Set([
      mapping.edgeSourceField,
      mapping.edgeTargetField,
      mapping.edgeTypeField
    ]));

    if (!nodeIds.has(source)) {
      nodes.push({ id: source, 'Node Type': 'Unknown' });
      nodeIds.add(source);
      warnings.push(`Created placeholder node for missing source "${source}".`);
    }
    if (!nodeIds.has(target)) {
      nodes.push({ id: target, 'Node Type': 'Unknown' });
      nodeIds.add(target);
      warnings.push(`Created placeholder node for missing target "${target}".`);
    }

    links.push({
      ...properties,
      source,
      target,
      'Edge Type': edgeType
    } as GraphLink);
  });

  if (nodes.length === 0) {
    throw new Error('No nodes could be imported with the selected schema.');
  }

  return {
    graphData: { nodes, links },
    warnings
  };
};
